// Browser entry for the relay's remote-control page.
//
// The same HTML serves two flows:
//
//   1. Registration. URL carries `#fp=<fingerprint>` — set by the desktop
//      Settings UI when the user clicks "Pair a new device". The browser
//      shows a code form, runs `navigator.credentials.create()` against the
//      desktop's challenge, and the desktop persists the new passkey.
//
//   2. Authenticated reconnect. URL has no fragment. The browser:
//        a. Tries `localStorage.fp` for a cached install fingerprint.
//        b. If absent, runs a discoverable-credential `navigator.credentials.get()`
//           with a throwaway challenge to learn the fingerprint from
//           `userHandle` and caches it.
//        c. Opens the WebSocket to `/c/<fp>`, runs the e2e Peer, sends
//           `pair:hello {mode:'authenticate'}`, and runs a second
//           `credentials.get()` against the desktop's challenge to produce
//           the assertion the desktop verifies.
//
// `localStorage.fp` is best-effort UX — clearing it just means the next
// reconnect prompts twice (once for discovery, once for the real assertion).
// It is NOT a security boundary; the desktop's WebAuthn assertion is the
// only thing that authenticates a returning client.

import {
  classifyRelayFrame,
  encodeBase64,
  isValidFingerprint,
  PAIR_PROTOCOL_VERSION,
  parsePairFrame,
  parseRemoteAppFrame,
  Peer,
  RELAY_PONG_FRAME,
  type RemoteAgentPresetSummary,
  type RemoteAppFrame,
  type RemoteProjectSummary,
  type RemoteWorkspaceSummary,
  type PairAuthChallenge,
  type PairFrame,
  type PairRegistrationChallenge,
} from '@baton/remote-protocol';
import { FitAddon } from '@xterm/addon-fit';
import { Terminal } from '@xterm/xterm';
import '@xterm/xterm/css/xterm.css';

import { floatToPcm16, startAudioCapture, type AudioCaptureHandle } from './audio-capture';
import { clientSocketUrl, parsePairUrl, PairUrlError } from './pair-url';
import {
  assertionToJSON,
  attestationToJSON,
  base64UrlToBytes,
  bytesToBase64Url,
  userHandleToFingerprint,
} from './webauthn';

const FP_CACHE_KEY = 'baton.remote.fp';
const REMOTE_LAUNCH_PREFERENCES_KEY = 'baton.remote.launchPreferences';
const REMOTE_RECONNECT_ATTEMPT_KEY = 'baton.remote.reconnectAttempt';
const REMOTE_REQUEST_TIMEOUT_MS = 15_000;
const REMOTE_RECONNECT_BASE_DELAY_MS = 1_000;
const REMOTE_RECONNECT_MAX_DELAY_MS = 10_000;
const MOBILE_TERMINAL_KEYS: Record<string, string> = {
  esc: '\x1b',
  slash: '/',
  tab: '\t',
  'ctrl-c': '\x03',
  'ctrl-d': '\x04',
  'ctrl-l': '\x0c',
  left: '\x1b[D',
  up: '\x1b[A',
  down: '\x1b[B',
  right: '\x1b[C',
  enter: '\r',
};

interface DirectRemoteConfig {
  fingerprint: string;
  wsUrl: string;
}

interface UI {
  title: HTMLElement;
  subtitle: HTMLElement;
  status: HTMLElement;
  fingerprintRow: HTMLElement;
  fingerprint: HTMLElement;
  form: HTMLElement;
  code: HTMLInputElement;
  label: HTMLInputElement;
  register: HTMLButtonElement;
  error: HTMLElement;
  success: HTMLElement;
}

interface RemoteUI {
  pairCard: HTMLElement;
  shell: HTMLElement;
  workspaceList: HTMLElement;
  openCreateWorkspace: HTMLButtonElement;
  voiceCreateWorkspace: HTMLButtonElement;
  workspaceVoice: HTMLElement;
  workspaceVoiceStatus: HTMLElement;
  workspaceVoiceMeter: HTMLElement;
  workspaceVoiceEdit: HTMLButtonElement;
  workspaceVoiceLaunch: HTMLButtonElement;
  workspaceVoiceCancel: HTMLButtonElement;
  createDialog: HTMLDialogElement;
  cancelCreateWorkspace: HTMLButtonElement;
  projectSelect: HTMLSelectElement;
  presetSelect: HTMLSelectElement;
  launchModeSelect: HTMLSelectElement;
  initialPrompt: HTMLTextAreaElement;
  createWorkspace: HTMLButtonElement;
  deleteDialog: HTMLDialogElement;
  deleteWorkspaceName: HTMLElement;
  cancelDeleteWorkspace: HTMLButtonElement;
  confirmDeleteWorkspace: HTMLButtonElement;
  backToWorkspaces: HTMLButtonElement;
  backToWorkspacesKeys: HTMLButtonElement;
  backToWorkspacesLive: HTMLButtonElement;
  terminalPanel: HTMLElement;
  terminalToolbar: HTMLElement;
  terminalSelect: HTMLSelectElement;
  terminalPrev: HTMLButtonElement;
  terminalNext: HTMLButtonElement;
  terminalStatusOverlay: HTMLElement;
  terminalStatusText: HTMLElement;
  terminalHost: HTMLElement;
  mobileKeyboardBar: HTMLElement;
  voiceBar: HTMLElement;
  voiceIdle: HTMLElement;
  voiceLive: HTMLElement;
  voiceRecord: HTMLButtonElement;
  voiceEnter: HTMLButtonElement;
  voiceAbort: HTMLButtonElement;
  voiceSend: HTMLButtonElement;
  voiceSendEnter: HTMLButtonElement;
  voiceMeter: HTMLElement;
  voiceStatus: HTMLElement;
}

interface LaunchPreferences {
  projectId?: string;
  presetId?: string;
  launchModeId?: string | null;
}

interface ReactNativeWebViewBridge {
  postMessage(message: string): void;
}

interface BatonNativeSessionReadyMessage {
  type: 'baton:remote-session-ready';
  url: string;
  label: string;
}

function ui(): UI {
  return {
    title: byId('page-title'),
    subtitle: byId('page-subtitle'),
    status: byId('status'),
    fingerprintRow: byId('fingerprint-row'),
    fingerprint: byId('fingerprint'),
    form: byId('register-form'),
    code: byId('code') as HTMLInputElement,
    label: byId('label') as HTMLInputElement,
    register: byId('register') as HTMLButtonElement,
    error: byId('error'),
    success: byId('success'),
  };
}

function remoteUi(): RemoteUI {
  return {
    pairCard: byId('pair-card'),
    shell: byId('remote-shell'),
    workspaceList: byId('workspace-list'),
    openCreateWorkspace: byId('open-create-workspace') as HTMLButtonElement,
    voiceCreateWorkspace: byId('voice-create-workspace') as HTMLButtonElement,
    workspaceVoice: byId('workspace-voice'),
    workspaceVoiceStatus: byId('workspace-voice-status'),
    workspaceVoiceMeter: byId('workspace-voice-meter'),
    workspaceVoiceEdit: byId('workspace-voice-edit') as HTMLButtonElement,
    workspaceVoiceLaunch: byId('workspace-voice-launch') as HTMLButtonElement,
    workspaceVoiceCancel: byId('workspace-voice-cancel') as HTMLButtonElement,
    createDialog: byId('create-workspace-dialog') as HTMLDialogElement,
    cancelCreateWorkspace: byId('cancel-create-workspace') as HTMLButtonElement,
    projectSelect: byId('project-select') as HTMLSelectElement,
    presetSelect: byId('preset-select') as HTMLSelectElement,
    launchModeSelect: byId('launch-mode-select') as HTMLSelectElement,
    initialPrompt: byId('initial-prompt') as HTMLTextAreaElement,
    createWorkspace: byId('create-workspace') as HTMLButtonElement,
    deleteDialog: byId('delete-workspace-dialog') as HTMLDialogElement,
    deleteWorkspaceName: byId('delete-workspace-name'),
    cancelDeleteWorkspace: byId('cancel-delete-workspace') as HTMLButtonElement,
    confirmDeleteWorkspace: byId('confirm-delete-workspace') as HTMLButtonElement,
    backToWorkspaces: byId('back-to-workspaces') as HTMLButtonElement,
    backToWorkspacesKeys: byId('back-to-workspaces-keys') as HTMLButtonElement,
    backToWorkspacesLive: byId('back-to-workspaces-live') as HTMLButtonElement,
    terminalPanel: byId('terminal-panel'),
    terminalToolbar: byId('terminal-toolbar'),
    terminalSelect: byId('terminal-select') as HTMLSelectElement,
    terminalPrev: byId('terminal-prev') as HTMLButtonElement,
    terminalNext: byId('terminal-next') as HTMLButtonElement,
    terminalStatusOverlay: byId('terminal-status-overlay'),
    terminalStatusText: byId('terminal-status-text'),
    terminalHost: byId('terminal'),
    mobileKeyboardBar: byId('mobile-keyboard-bar'),
    voiceBar: byId('voice-bar'),
    voiceIdle: byId('voice-idle'),
    voiceLive: byId('voice-live'),
    voiceRecord: byId('voice-record') as HTMLButtonElement,
    voiceEnter: byId('voice-enter') as HTMLButtonElement,
    voiceAbort: byId('voice-abort') as HTMLButtonElement,
    voiceSend: byId('voice-send') as HTMLButtonElement,
    voiceSendEnter: byId('voice-send-enter') as HTMLButtonElement,
    voiceMeter: byId('voice-meter'),
    voiceStatus: byId('voice-status'),
  };
}

function byId(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing #${id}`);
  return el;
}

type StatusKind =
  | 'idle'
  | 'connecting'
  | 'ready'
  | 'verifying'
  | 'registered'
  | 'authenticated'
  | 'error';

type RemoteTerminalSummary = RemoteWorkspaceSummary['terminals'][number];

function setStatus(el: HTMLElement, kind: StatusKind, text: string): void {
  el.className = `value status status-${kind}`;
  el.textContent = text;
}

function showError(els: UI, message: string): void {
  els.error.textContent = message;
  els.error.hidden = false;
  els.success.hidden = true;
}

function showSuccess(els: UI, message: string): void {
  els.success.textContent = message;
  els.success.hidden = false;
  els.error.hidden = true;
}

function shortFp(fp: string): string {
  return `${fp.slice(0, 12)}…${fp.slice(-4)}`;
}

function readDirectRemoteConfig(): DirectRemoteConfig | null {
  const candidate = (window as Window & { BATON_REMOTE_DIRECT?: unknown }).BATON_REMOTE_DIRECT;
  if (!candidate || typeof candidate !== 'object') return null;
  const { fingerprint, wsUrl } = candidate as { fingerprint?: unknown; wsUrl?: unknown };
  if (typeof fingerprint !== 'string' || !isValidFingerprint(fingerprint)) return null;
  if (typeof wsUrl !== 'string' || !/^wss?:\/\//.test(wsUrl)) return null;
  return { fingerprint, wsUrl };
}

function isDirectMode(): boolean {
  return readDirectRemoteConfig() !== null;
}

function notifyNativeSessionReady(label: string): void {
  const bridge = (window as Window & { ReactNativeWebView?: ReactNativeWebViewBridge })
    .ReactNativeWebView;
  if (!bridge) return;
  const message: BatonNativeSessionReadyMessage = {
    type: 'baton:remote-session-ready',
    url: `${location.origin}/remote/`,
    label,
  };
  bridge.postMessage(JSON.stringify(message));
}

function socketUrlForFingerprint(
  fingerprint: string,
  parsed?: ReturnType<typeof parsePairUrl>
): string {
  const direct = readDirectRemoteConfig();
  if (direct) return direct.wsUrl;
  if (parsed) return clientSocketUrl(parsed);
  return `${location.origin.replace(/^http/, 'ws')}/c/${fingerprint}`;
}

function handleSocketMessage(evt: MessageEvent, ws: WebSocket, peer: Peer): void {
  const text = typeof evt.data === 'string' ? evt.data : '';
  const relay = classifyRelayFrame(text);
  if (relay === 'ping') {
    ws.send(RELAY_PONG_FRAME);
    return;
  }
  if (relay === 'pong') return;
  peer.handleMessage(text);
}

function deriveDefaultLabel(): string {
  const ua = navigator.userAgent;
  const browser = ua.includes('Edg/')
    ? 'Edge'
    : ua.includes('Firefox/')
      ? 'Firefox'
      : ua.includes('Chrome/')
        ? 'Chrome'
        : ua.includes('Safari/')
          ? 'Safari'
          : 'Browser';
  const os = ua.includes('Mac OS X')
    ? 'macOS'
    : ua.includes('Windows')
      ? 'Windows'
      : ua.includes('Android')
        ? 'Android'
        : ua.includes('iPhone') || ua.includes('iPad')
          ? 'iOS'
          : ua.includes('Linux')
            ? 'Linux'
            : 'this device';
  return `${browser} on ${os}`;
}

class RemoteAppClient {
  private readonly els = remoteUi();
  private readonly term = new Terminal({
    cursorBlink: true,
    convertEol: false,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 13,
    theme: {
      background: '#07090d',
      foreground: '#e6e9ef',
      cursor: '#e6e9ef',
      selectionBackground: '#27446f',
    },
  });
  private readonly fitAddon = new FitAddon();
  private workspaces: RemoteWorkspaceSummary[] = [];
  private projects: RemoteProjectSummary[] = [];
  private presets: RemoteAgentPresetSummary[] = [];
  private defaultLaunchModeId: string | undefined;
  private launchPreferences: LaunchPreferences = readLaunchPreferences();
  private selectedWorkspaceId: string | null = null;
  private attachedTerminalId: string | null = null;
  private pendingAttachWorkspaceId: string | null = null;
  private pendingCreateRequestId: string | null = null;
  private pendingCreateWorkspace: RemoteWorkspaceSummary | null = null;
  private deleteCandidateWorkspaceId: string | null = null;
  private pendingDeleteWorkspaceId: string | null = null;
  private readonly pendingWorkspaceMessages = new Map<string, string>();
  private readonly pendingRequestWorkspaces = new Map<string, string>();
  private readonly pendingRequestTimers = new Map<string, number>();
  private readonly resizeObserver: ResizeObserver | null = null;
  private lastSentTerminalSize: string | null = null;
  private lastTerminalSeq = -1;
  private nextRequestId = 1;
  private workspaceRefreshTimer: number | null = null;
  private disconnected = false;
  private voiceAvailable = false;
  private voiceCapture: AudioCaptureHandle | null = null;
  private voiceLevelTimer: number | null = null;
  private voiceStarting = false;
  private voiceStatusTimer: number | null = null;
  private pendingVoiceRequestId: string | null = null;
  private pendingWorkspaceVoiceAction: 'launch' | 'edit' | null = null;
  private voiceTarget: 'terminal' | 'workspace' | null = null;
  private keyboardOpen = false;
  private readonly mobileWorkspaceQuery = window.matchMedia('(max-width: 760px)');

  constructor(private readonly peer: Peer) {
    this.term.loadAddon(this.fitAddon);
    this.term.open(this.els.terminalHost);
    this.configureMobileTerminalInput();
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.fitAndSync());
      this.resizeObserver.observe(this.els.terminalHost);
    }
    this.term.onData((data) => {
      this.sendTerminalInput(data);
    });
    this.bindMobileKeyboardBar();
    this.bindVoiceControls();
    this.bindVisualViewport();
    this.els.openCreateWorkspace.addEventListener('click', () => this.openCreateDialog());
    this.els.voiceCreateWorkspace.addEventListener('click', () => void this.startWorkspaceVoice());
    this.els.workspaceVoiceLaunch.addEventListener('click', () =>
      this.stopAndUseWorkspaceVoice('launch')
    );
    this.els.workspaceVoiceEdit.addEventListener('click', () => this.stopAndUseWorkspaceVoice('edit'));
    this.els.workspaceVoiceCancel.addEventListener('click', () => this.cancelVoiceRecording());
    this.els.cancelCreateWorkspace.addEventListener('click', () => this.els.createDialog.close());
    this.els.cancelDeleteWorkspace.addEventListener('click', () => this.closeDeleteDialog());
    this.els.confirmDeleteWorkspace.addEventListener('click', () => this.deleteConfirmedWorkspace());
    this.els.projectSelect.addEventListener('change', () => this.persistLaunchPreferences());
    this.els.presetSelect.addEventListener('change', () => {
      this.renderLaunchModes(true);
      this.persistLaunchPreferences();
    });
    this.els.launchModeSelect.addEventListener('change', () => this.persistLaunchPreferences());
    this.els.createWorkspace.addEventListener('click', () => this.createWorkspace());
    this.els.backToWorkspaces.addEventListener('click', () => this.showWorkspaceList());
    this.els.backToWorkspacesKeys.addEventListener('click', () => this.showWorkspaceList());
    this.els.backToWorkspacesLive.addEventListener('click', () => this.showWorkspaceList());
    this.els.terminalPrev.addEventListener('click', () => this.cycleTerminal(-1));
    this.els.terminalNext.addEventListener('click', () => this.cycleTerminal(1));
    this.els.terminalSelect.addEventListener('change', () => {
      const terminalId = this.els.terminalSelect.value;
      if (this.selectedWorkspaceId && terminalId) this.attachWorkspace(this.selectedWorkspaceId, terminalId);
    });
    this.mobileWorkspaceQuery.addEventListener('change', () => this.renderWorkspaces());
    window.addEventListener('resize', () => this.fitAndSync());
    this.peer.onMessage((msg) => {
      if (msg.kind !== 'json') return;
      const frame = parseRemoteAppFrame(msg.data);
      if (!frame) return;
      this.handleFrame(frame);
    });
    this.peer.onClose(() => this.markDisconnected());
  }

  start(label: string): void {
    this.els.pairCard.hidden = true;
    this.els.shell.hidden = false;
    document.body.classList.add('remote-active');
    document.title = `Baton Remote - ${label}`;
    resetPageReconnectAttempts();
    this.showWorkspaceList();
    this.getLaunchOptions();
    this.listWorkspaces();
    this.queryVoiceAvailability();
    this.fitAfterLayout();
  }

  private queryVoiceAvailability(): void {
    // getUserMedia needs a secure context (HTTPS, or localhost). Without one
    // the mic button would be dead, so never offer it.
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) return;
    this.sendRemoteFrame({
      type: 'app:voice:status',
      requestId: this.requestId(),
    });
  }

  private requestId(): string {
    return `web-${this.nextRequestId++}`;
  }

  private listWorkspaces(): void {
    this.sendRemoteFrame({
      type: 'app:workspaces:list',
      requestId: this.requestId(),
    });
  }

  private getLaunchOptions(): void {
    this.sendRemoteFrame({
      type: 'app:launch-options:get',
      requestId: this.requestId(),
    });
  }

  private createWorkspace(promptOverride?: string): void {
    if (!this.ensureConnected()) return;
    this.renderLaunchOptions();
    const projectId = this.els.projectSelect.value;
    if (!projectId) {
      return;
    }
    const initialPrompt = (promptOverride ?? this.els.initialPrompt.value.trim()) || undefined;
    this.setCreateWorkspacePending(true);
    const requestId = this.requestId();
    const project = this.projects.find((candidate) => candidate.id === projectId);
    this.pendingCreateRequestId = requestId;
    this.pendingCreateWorkspace = {
      id: `pending-${requestId}`,
      name: 'New workspace',
      workingDirectory: '',
      projectId,
      ...(project?.name ? { projectName: project.name } : {}),
      ...(project?.color ? { projectColor: project.color } : {}),
      status: 'active',
      provisioning: true,
      createdAt: Date.now(),
      lastActivityAt: Date.now(),
      terminals: [],
    };
    this.renderWorkspaces();
    this.trackPendingRequest(requestId, this.pendingCreateWorkspace.id, 'Launch timed out.');
    this.sendRemoteFrame(
      {
        type: 'app:workspace:create',
        requestId,
        projectId,
        presetId: this.els.presetSelect.value || undefined,
        launchModeId: this.els.launchModeSelect.value || undefined,
        initialPrompt,
      },
      {
        requestId,
      }
    );
  }

  private handleFrame(frame: RemoteAppFrame): void {
    if (this.disconnected) return;
    switch (frame.type) {
      case 'app:workspaces':
        this.workspaces = this.withPendingCreate(frame.workspaces);
        if (
          this.selectedWorkspaceId &&
          !this.workspaces.some((workspace) => workspace.id === this.selectedWorkspaceId)
        ) {
          this.selectedWorkspaceId = null;
          this.attachedTerminalId = null;
          this.pendingAttachWorkspaceId = null;
          this.clearTerminalLoading();
        }
        this.renderWorkspaces();
        this.renderTerminalToolbar();
        if (this.selectedWorkspaceId && !this.attachedTerminalId) {
          const selected = this.workspaces.find(
            (workspace) => workspace.id === this.selectedWorkspaceId
          );
          if (
            selected &&
            this.terminalIdForWorkspace(selected) &&
            this.pendingAttachWorkspaceId !== selected.id
          ) {
            this.attachWorkspace(selected.id);
          } else if (selected?.provisioning || selected?.terminals.length === 0) {
            this.waitForWorkspace(selected.id);
          }
        }
        return;
      case 'app:launch-options':
        this.projects = frame.projects;
        this.presets = frame.presets;
        this.defaultLaunchModeId = frame.defaultLaunchModeId;
        this.renderLaunchOptions();
        return;
      case 'app:workspace:created':
        this.setCreateWorkspacePending(false);
        this.clearPendingRequest(frame.requestId);
        this.pendingCreateRequestId = null;
        this.pendingCreateWorkspace = null;
        this.els.initialPrompt.value = '';
        this.els.createDialog.close();
        this.workspaces = sortWorkspaces([
          frame.workspace,
          ...this.workspaces.filter((w) => w.id !== frame.workspace.id),
        ]);
        this.renderWorkspaces();
        return;
      case 'app:workspace:started':
        this.clearPendingRequest(frame.requestId);
        this.workspaces = sortWorkspaces([
          frame.workspace,
          ...this.workspaces.filter((w) => w.id !== frame.workspace.id),
        ]);
        this.renderWorkspaces();
        this.renderTerminalToolbar();
        if (this.selectedWorkspaceId === frame.workspace.id) {
          this.attachOrWaitForWorkspace(frame.workspace.id);
        }
        return;
      case 'app:workspace:stopped':
        this.clearPendingRequest(frame.requestId);
        if (this.selectedWorkspaceId === frame.workspace.id) {
          this.selectedWorkspaceId = null;
          this.pendingAttachWorkspaceId = null;
          this.attachedTerminalId = null;
          this.clearTerminalLoading();
          this.showWorkspaceList();
        }
        this.workspaces = sortWorkspaces([
          frame.workspace,
          ...this.workspaces.filter((w) => w.id !== frame.workspace.id),
        ]);
        this.renderWorkspaces();
        this.renderTerminalToolbar();
        return;
      case 'app:workspace:deleted':
        this.clearPendingRequest(frame.requestId);
        if (this.pendingDeleteWorkspaceId === frame.workspaceId) {
          this.pendingDeleteWorkspaceId = null;
        }
        if (this.selectedWorkspaceId === frame.workspaceId) {
          this.selectedWorkspaceId = null;
          this.pendingAttachWorkspaceId = null;
          this.attachedTerminalId = null;
          this.clearTerminalLoading();
          this.showWorkspaceList();
        }
        this.workspaces = this.workspaces.filter((workspace) => workspace.id !== frame.workspaceId);
        this.renderWorkspaces();
        this.renderTerminalToolbar();
        return;
      case 'app:workspace:title:regenerated':
        this.clearPendingRequest(frame.requestId);
        this.workspaces = sortWorkspaces([
          frame.workspace,
          ...this.workspaces.filter((w) => w.id !== frame.workspace.id),
        ]);
        this.renderWorkspaces();
        return;
      case 'app:terminal:attached':
        if (
          frame.snapshot.workspaceId !== this.selectedWorkspaceId ||
          frame.snapshot.workspaceId !== this.pendingAttachWorkspaceId
        ) {
          return;
        }
        this.attachedTerminalId = frame.snapshot.terminalId;
        this.pendingAttachWorkspaceId = null;
        this.pendingWorkspaceMessages.delete(frame.snapshot.workspaceId);
        this.clearPendingRequest(frame.requestId);
        this.selectedWorkspaceId = frame.snapshot.workspaceId;
        this.lastTerminalSeq = frame.snapshot.seq;
        this.clearTerminalLoading();
        this.term.reset();
        this.renderWorkspaces();
        this.renderTerminalToolbar();
        this.updateVoiceUi();
        this.fitAndSync();
        this.term.write(frame.snapshot.buffer, () => {
          this.fitAndSync();
          this.term.scrollToBottom();
        });
        this.term.focus();
        return;
      case 'app:terminal:data':
        if (frame.terminalId === this.attachedTerminalId && frame.seq > this.lastTerminalSeq) {
          this.lastTerminalSeq = frame.seq;
          this.term.write(frame.data, () => this.term.scrollToBottom());
        }
        return;
      case 'app:terminal:status':
        this.updateTerminalStatus(frame.workspaceId, frame.terminalId, frame.status);
        return;
      case 'app:voice:availability':
        this.voiceAvailable = frame.available;
        this.updateVoiceUi();
        this.updateWorkspaceVoiceUi();
        // The bar appearing/disappearing changes the terminal's height.
        if (this.attachedTerminalId) this.fitAndSync();
        return;
      case 'app:voice:transcribed':
        this.handleWorkspaceVoiceTranscript(frame.requestId, frame.text);
        return;
      case 'app:error':
        this.setCreateWorkspacePending(false);
        if (this.pendingDeleteWorkspaceId) {
          this.pendingDeleteWorkspaceId = null;
        }
        if (frame.requestId === this.pendingCreateRequestId) {
          this.pendingCreateRequestId = null;
          this.pendingCreateWorkspace = null;
        }
        if (frame.requestId && frame.requestId === this.pendingVoiceRequestId) {
          this.pendingVoiceRequestId = null;
          this.pendingWorkspaceVoiceAction = null;
          this.setVoiceStatus('Dictation failed', 2500);
          this.setWorkspaceVoiceStatus('Dictation failed');
          this.updateVoiceUi();
          this.updateWorkspaceVoiceUi();
        }
        this.clearPendingRequest(frame.requestId);
        this.pendingAttachWorkspaceId = null;
        this.clearTerminalLoading();
        this.renderWorkspaces();
        this.renderTerminalToolbar();
        console.warn(frame.message);
        return;
      default:
        return;
    }
  }

  private openWorkspace(workspaceId: string): void {
    if (!this.ensureConnected()) return;
    const workspace = this.workspaces.find((candidate) => candidate.id === workspaceId);
    if (!workspace) return;
    this.clearWorkspaceAttentionLocal(workspaceId);
    this.showTerminal();
    if (this.selectedWorkspaceId === workspaceId && this.attachedTerminalId) {
      this.term.focus();
      return;
    }
    if (this.pendingAttachWorkspaceId === workspaceId) {
      return;
    }
    if (workspace.provisioning) {
      this.waitForWorkspace(workspaceId);
      return;
    }
    if (this.terminalIdForWorkspace(workspace)) {
      this.attachWorkspace(workspaceId);
      return;
    }
    this.startWorkspace(workspaceId);
  }

  private attachOrWaitForWorkspace(workspaceId: string): void {
    const workspace = this.workspaces.find((candidate) => candidate.id === workspaceId);
    if (!workspace) return;
    if (this.terminalIdForWorkspace(workspace)) {
      this.attachWorkspace(workspaceId);
    } else {
      this.waitForWorkspace(workspaceId);
    }
  }

  private startWorkspace(workspaceId: string): void {
    const workspace = this.workspaces.find((candidate) => candidate.id === workspaceId);
    if (!workspace) return;
    this.selectedWorkspaceId = workspaceId;
    this.attachedTerminalId = null;
    this.lastSentTerminalSize = null;
    this.pendingAttachWorkspaceId = null;
    this.prepareTerminalTransition('Starting workspace...');
    this.renderWorkspaces();
    this.renderTerminalToolbar();
    const requestId = this.requestId();
    this.trackPendingRequest(requestId, workspaceId, 'Start timed out. Tap to retry.');
    this.sendRemoteFrame(
      {
        type: 'app:workspace:start',
        requestId,
        workspaceId,
      },
      { requestId, workspaceId }
    );
  }

  private restartWorkspace(workspaceId: string): void {
    if (!this.ensureConnected()) return;
    const workspace = this.workspaces.find((candidate) => candidate.id === workspaceId);
    if (!workspace || workspace.provisioning) return;
    this.selectedWorkspaceId = workspaceId;
    this.attachedTerminalId = null;
    this.lastSentTerminalSize = null;
    this.pendingAttachWorkspaceId = null;
    this.prepareTerminalTransition('Restarting workspace...');
    this.showTerminal();
    this.renderWorkspaces();
    this.renderTerminalToolbar();
    const requestId = this.requestId();
    this.trackPendingRequest(requestId, workspaceId, 'Restart timed out. Tap to retry.');
    this.sendRemoteFrame(
      {
        type: 'app:workspace:start',
        requestId,
        workspaceId,
      },
      { requestId, workspaceId }
    );
  }

  private stopWorkspace(workspaceId: string): void {
    if (!this.ensureConnected()) return;
    const workspace = this.workspaces.find((candidate) => candidate.id === workspaceId);
    if (!workspace || workspace.provisioning || !this.terminalIdForWorkspace(workspace)) return;
    if (this.selectedWorkspaceId === workspaceId) {
      this.cancelVoiceRecording();
      this.attachedTerminalId = null;
      this.pendingAttachWorkspaceId = null;
      this.clearTerminalLoading();
      this.showWorkspaceList();
    }
    this.pendingWorkspaceMessages.set(workspaceId, 'Stopping workspace...');
    this.renderWorkspaces();
    const requestId = this.requestId();
    this.trackPendingRequest(requestId, workspaceId, 'Stop timed out. Try again.');
    this.sendRemoteFrame(
      {
        type: 'app:workspace:stop',
        requestId,
        workspaceId,
      },
      { requestId, workspaceId }
    );
  }

  private regenerateWorkspaceTitle(workspaceId: string): void {
    if (!this.ensureConnected()) return;
    const workspace = this.workspaces.find((candidate) => candidate.id === workspaceId);
    if (!workspace || workspace.provisioning) return;
    this.pendingWorkspaceMessages.set(workspaceId, 'Regenerating title...');
    this.renderWorkspaces();
    const requestId = this.requestId();
    this.trackPendingRequest(requestId, workspaceId, 'Title regenerate timed out. Try again.');
    this.sendRemoteFrame(
      {
        type: 'app:workspace:title:regenerate',
        requestId,
        workspaceId,
      },
      { requestId, workspaceId }
    );
  }

  private openDeleteDialog(workspaceId: string): void {
    const workspace = this.workspaces.find((candidate) => candidate.id === workspaceId);
    if (!workspace || workspace.provisioning) return;
    this.deleteCandidateWorkspaceId = workspaceId;
    this.els.deleteWorkspaceName.textContent = `Delete "${workspace.name}"? This removes the workspace and its worktree.`;
    this.els.deleteDialog.showModal();
  }

  private closeDeleteDialog(): void {
    this.deleteCandidateWorkspaceId = null;
    this.els.deleteDialog.close();
  }

  private deleteConfirmedWorkspace(): void {
    if (!this.ensureConnected()) return;
    const workspaceId = this.deleteCandidateWorkspaceId;
    if (!workspaceId) return;
    const workspace = this.workspaces.find((candidate) => candidate.id === workspaceId);
    if (!workspace || workspace.provisioning) return;
    this.deleteCandidateWorkspaceId = null;
    this.pendingDeleteWorkspaceId = workspaceId;
    this.els.deleteDialog.close();
    if (this.selectedWorkspaceId === workspaceId) {
      this.cancelVoiceRecording();
      this.attachedTerminalId = null;
      this.pendingAttachWorkspaceId = null;
      this.clearTerminalLoading();
      this.showWorkspaceList();
    }
    this.pendingWorkspaceMessages.set(workspaceId, 'Deleting workspace...');
    this.renderWorkspaces();
    const requestId = this.requestId();
    this.trackPendingRequest(requestId, workspaceId, 'Delete timed out. Try again.');
    this.sendRemoteFrame(
      {
        type: 'app:workspace:delete',
        requestId,
        workspaceId,
      },
      { requestId, workspaceId }
    );
  }

  private attachWorkspace(workspaceId: string, requestedTerminalId?: string): void {
    const workspace = this.workspaces.find((candidate) => candidate.id === workspaceId);
    if (!workspace || workspace.provisioning) return;
    const terminalId = requestedTerminalId ?? this.terminalIdForWorkspace(workspace);
    if (!terminalId) {
      this.waitForWorkspace(workspaceId);
      return;
    }
    if (
      this.selectedWorkspaceId === workspaceId &&
      this.attachedTerminalId === terminalId &&
      this.pendingAttachWorkspaceId === null
    ) {
      this.showTerminal();
      this.term.focus();
      return;
    }
    this.clearWorkspaceRefreshTimer();
    this.clearWorkspaceAttentionLocal(workspaceId, terminalId);
    this.selectedWorkspaceId = workspaceId;
    this.pendingAttachWorkspaceId = workspaceId;
    this.prepareTerminalTransition('Attaching terminal...');
    this.renderWorkspaces();
    this.renderTerminalToolbar();
    const requestId = this.requestId();
    this.trackPendingRequest(requestId, workspaceId, 'Attach timed out. Tap to retry.');
    const dimensions = this.fitAndSync();
    if (dimensions) {
      this.lastSentTerminalSize = this.sizeKey(dimensions);
    }
    this.sendRemoteFrame(
      {
        type: 'app:terminal:attach',
        requestId,
        workspaceId,
        terminalId,
        ...(dimensions ? { cols: dimensions.cols, rows: dimensions.rows } : {}),
      },
      { requestId, workspaceId }
    );
  }

  private renderWorkspaces(): void {
    let running = sortWorkspaces(
      this.workspaces.filter((workspace) => this.isWorkspaceRunning(workspace))
    );
    let inactive = sortWorkspaces(
      this.workspaces.filter((workspace) => !this.isWorkspaceRunning(workspace))
    );
    const mobileBottomUp = this.mobileWorkspaceQuery.matches;
    if (mobileBottomUp) {
      running = [...running].reverse();
      inactive = [...inactive].reverse();
    }
    const nodes: HTMLElement[] = [];
    const groups = [
      ...(running.length > 0 ? [this.renderWorkspaceGroup('Active', running)] : []),
      ...(inactive.length > 0 ? [this.renderWorkspaceGroup('Inactive', inactive)] : []),
    ];
    nodes.push(...(mobileBottomUp ? groups.reverse() : groups));
    if (nodes.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'workspace-empty';
      empty.textContent = 'No workspaces yet.';
      nodes.push(empty);
    }
    this.els.workspaceList.replaceChildren(...nodes);
    if (mobileBottomUp) {
      requestAnimationFrame(() => {
        this.els.workspaceList.scrollTop = this.els.workspaceList.scrollHeight;
      });
    }
  }

  private renderWorkspaceGroup(label: string, workspaces: RemoteWorkspaceSummary[]): HTMLElement {
    const group = document.createElement('section');
    group.className = 'workspace-group';
    const heading = document.createElement('h2');
    heading.textContent = label;
    const count = document.createElement('span');
    count.textContent = String(workspaces.length);
    heading.append(count);
    group.append(heading, ...workspaces.map((workspace) => this.renderWorkspaceCard(workspace)));
    return group;
  }

  private renderWorkspaceCard(workspace: RemoteWorkspaceSummary): HTMLElement {
    const row = document.createElement('article');
    const isSelected =
      workspace.id === this.selectedWorkspaceId &&
      (this.attachedTerminalId !== null || this.pendingAttachWorkspaceId === workspace.id);
    const isRunnable = Boolean(this.terminalIdForWorkspace(workspace));
    const isStopped = !isRunnable && !workspace.provisioning;
    const pendingMessage = this.pendingMessageForWorkspace(workspace);
    row.className = [
      'workspace',
      isSelected ? 'active' : '',
      isRunnable ? 'running' : 'stopped',
      pendingMessage ? 'pending' : '',
    ]
      .filter(Boolean)
      .join(' ');
    if (workspace.projectColor) {
      row.style.setProperty('--workspace-accent', workspace.projectColor);
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'workspace-open';
    button.disabled = workspace.id === this.pendingCreateWorkspace?.id;
    const attentionTooltip = this.workspaceAttentionTooltip(workspace);
    button.title = attentionTooltip || (isStopped ? 'Click to start this workspace' : '');
    const meta = workspace.branchName ?? workspace.workingDirectory ?? 'Provisioning';
    const project = workspace.projectName
      ? `<small class="workspace-project">${escapeHtml(workspace.projectName)}</small>`
      : '';
    const notice = pendingMessage
      ? `<small class="workspace-notice"><i aria-hidden="true"></i>${escapeHtml(pendingMessage)}</small>`
      : '';
    const menu = this.renderWorkspaceMenu(workspace, isRunnable);
    const menuButton = menu.querySelector<HTMLButtonElement>('.workspace-menu-trigger');
    let suppressNextClick = false;
    let longPressTimer: number | null = null;
    const clearLongPress = () => {
      if (longPressTimer) {
        window.clearTimeout(longPressTimer);
        longPressTimer = null;
      }
    };
    button.innerHTML = `${project}<span class="workspace-title"><span class="workspace-name">${escapeHtml(workspace.name)}</span>${this.renderWorkspaceAdornment(workspace)}</span><small class="workspace-meta">${escapeHtml(meta)}</small>${this.renderWorkspaceFooter(workspace)}${notice}`;
    button.addEventListener('click', (event) => {
      if (suppressNextClick) {
        event.preventDefault();
        suppressNextClick = false;
        return;
      }
      this.openWorkspace(workspace.id);
    });

    row.addEventListener('contextmenu', (event) => {
      event.preventDefault();
      this.openWorkspaceMenu(row, menu, event.clientX, event.clientY);
    });
    menuButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const rect = menuButton.getBoundingClientRect();
      this.openWorkspaceMenu(row, menu, rect.right, rect.bottom);
    });
    row.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' || this.mobileWorkspaceQuery.matches) return;
      const { clientX, clientY } = event;
      clearLongPress();
      longPressTimer = window.setTimeout(() => {
        longPressTimer = null;
        suppressNextClick = true;
        this.openWorkspaceMenu(row, menu, clientX, clientY);
      }, 520);
    });
    row.addEventListener('pointerup', clearLongPress);
    row.addEventListener('pointerleave', clearLongPress);
    row.addEventListener('pointercancel', clearLongPress);
    row.append(button, menu);
    return row;
  }

  private renderWorkspaceMenu(workspace: RemoteWorkspaceSummary, isRunnable: boolean): HTMLElement {
    const menu = document.createElement('div');
    menu.className = 'workspace-menu';
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'workspace-menu-trigger';
    trigger.setAttribute('aria-label', `${workspace.name} actions`);
    trigger.textContent = '⋯';
    const actions = document.createElement('div');
    actions.className = 'workspace-menu-popover';
    actions.setAttribute('role', 'menu');
    actions.setAttribute('aria-label', `${workspace.name} actions`);

    const restart = document.createElement('button');
    restart.type = 'button';
    restart.textContent = isRunnable ? 'Restart' : 'Start';
    restart.disabled = workspace.provisioning;
    restart.addEventListener('click', (event) => {
      event.preventDefault();
      menu.classList.remove('open');
      if (isRunnable) {
        this.restartWorkspace(workspace.id);
      } else {
        this.openWorkspace(workspace.id);
      }
    });

    const stop = document.createElement('button');
    stop.type = 'button';
    stop.textContent = 'Stop';
    stop.disabled = !isRunnable || workspace.provisioning;
    stop.addEventListener('click', (event) => {
      event.preventDefault();
      menu.classList.remove('open');
      this.stopWorkspace(workspace.id);
    });

    const regenerate = document.createElement('button');
    regenerate.type = 'button';
    regenerate.textContent = 'Regenerate title';
    regenerate.disabled = workspace.provisioning;
    regenerate.addEventListener('click', (event) => {
      event.preventDefault();
      menu.classList.remove('open');
      this.regenerateWorkspaceTitle(workspace.id);
    });

    const deleteWorkspace = document.createElement('button');
    deleteWorkspace.type = 'button';
    deleteWorkspace.className = 'danger-action';
    deleteWorkspace.textContent = 'Delete';
    deleteWorkspace.disabled = workspace.provisioning;
    deleteWorkspace.addEventListener('click', (event) => {
      event.preventDefault();
      menu.classList.remove('open');
      this.openDeleteDialog(workspace.id);
    });

    actions.append(restart, stop, regenerate, deleteWorkspace);
    menu.append(trigger, actions);
    return menu;
  }

  private openWorkspaceMenu(
    row: HTMLElement,
    menu: HTMLElement,
    clientX: number,
    clientY: number
  ): void {
    for (const openMenu of this.els.workspaceList.querySelectorAll('.workspace-menu.open')) {
      openMenu.classList.remove('open');
    }
    const rect = row.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 8), Math.max(rect.width - 168, 8));
    const y = Math.max(clientY - rect.top, 8);
    menu.style.setProperty('--menu-x', `${x}px`);
    menu.style.setProperty('--menu-y', `${y}px`);
    menu.classList.add('open');
    const close = (event: MouseEvent) => {
      if (!menu.contains(event.target as Node)) menu.classList.remove('open');
    };
    window.setTimeout(() => document.addEventListener('click', close, { once: true }), 0);
  }

  private isWorkspaceRunning(workspace: RemoteWorkspaceSummary): boolean {
    return workspace.provisioning || Boolean(this.terminalIdForWorkspace(workspace));
  }

  private renderWorkspaceFooter(workspace: RemoteWorkspaceSummary): string {
    const git = workspace.git;
    const badges: string[] = [];
    if (git && (git.additions > 0 || git.deletions > 0)) {
      badges.push(`<b class="git-add">+${git.additions}</b>`);
      badges.push(`<b class="git-del">-${git.deletions}</b>`);
    }
    if (git?.aheadTargetBranch) {
      badges.push(`<b class="git-ahead">↑${git.aheadTargetBranch}</b>`);
    }
    if (git?.behindTargetBranch) {
      badges.push(`<b class="git-behind">↓${git.behindTargetBranch}</b>`);
    }
    const activity = workspace.lastActivityAt ? formatRelativeTime(workspace.lastActivityAt) : '';
    if (!badges.length && !activity) return '';
    return `<span class="workspace-footer">${
      badges.length ? `<small class="workspace-stats">${badges.join('')}</small>` : '<span></span>'
    }${activity ? `<small class="workspace-activity">${escapeHtml(activity)}</small>` : ''}</span>`;
  }

  private renderWorkspaceAdornment(workspace: RemoteWorkspaceSummary): string {
    if (workspace.attention) {
      const label = attentionLabel(workspace.attention.kind);
      return `<b class="workspace-badge ${attentionClassName(workspace.attention.kind)}">${escapeHtml(label)}</b>`;
    }
    if (workspace.agentActive) {
      return '<i class="workspace-spinner" aria-label="Agent processing"></i>';
    }
    return '';
  }

  private workspaceAttentionTooltip(workspace: RemoteWorkspaceSummary): string {
    if (!workspace.attention) return '';
    return [attentionLabel(workspace.attention.kind), workspace.attention.message]
      .filter(Boolean)
      .join(': ');
  }

  private openCreateDialog(): void {
    if (this.projects.length === 0 || this.presets.length === 0) {
      this.getLaunchOptions();
    }
    this.renderLaunchOptions();
    this.els.createDialog.showModal();
  }

  private renderLaunchOptions(): void {
    const currentProjectId = this.validProjectId(this.els.projectSelect.value);
    const storedProjectId = this.validProjectId(this.launchPreferences.projectId);
    const selectedWorkspaceProjectId = this.validProjectId(
      this.workspaces.find((workspace) => workspace.id === this.selectedWorkspaceId)?.projectId
    );
    const selectedProjectId =
      currentProjectId || storedProjectId || selectedWorkspaceProjectId || this.projects[0]?.id || '';
    this.els.projectSelect.replaceChildren(
      ...this.projects.map((project) => option(project.id, project.name, selectedProjectId))
    );

    const selectedPresetId =
      this.validPresetId(this.els.presetSelect.value) ||
      this.validPresetId(this.launchPreferences.presetId) ||
      this.presets[0]?.id ||
      '';
    this.els.presetSelect.replaceChildren(
      ...this.presets.map((preset) => option(preset.id, preset.name, selectedPresetId))
    );
    this.renderLaunchModes();
    this.persistLaunchPreferences();
  }

  private renderLaunchModes(resetToPreferred = false): void {
    const preset = this.presets.find((candidate) => candidate.id === this.els.presetSelect.value);
    const currentModeId = resetToPreferred
      ? undefined
      : this.validLaunchModeId(preset, selectValueToLaunchModeId(this.els.launchModeSelect.value));
    const storedModeId = resetToPreferred
      ? undefined
      : this.validLaunchModeId(preset, this.launchPreferences.launchModeId);
    const selectedModeId =
      launchModeIdToSelectValue(
        currentModeId ?? storedModeId ?? this.resolvePreferredLaunchModeId(preset)
      ) ||
      '';
    this.els.launchModeSelect.replaceChildren(
      option('', 'Default', selectedModeId),
      ...(preset?.launchModes.map((mode) => option(mode.id, mode.name, selectedModeId)) ?? [])
    );
  }

  private resolvePreferredLaunchModeId(
    preset: RemoteAgentPresetSummary | undefined
  ): string | null {
    if (!preset) return null;
    if (preset.lastLaunchModeId !== undefined) {
      return this.validLaunchModeId(preset, preset.lastLaunchModeId) ?? null;
    }
    return this.validLaunchModeId(preset, this.defaultLaunchModeId) ?? null;
  }

  private validProjectId(projectId: string | null | undefined): string | undefined {
    return projectId && this.projects.some((project) => project.id === projectId)
      ? projectId
      : undefined;
  }

  private validPresetId(presetId: string | null | undefined): string | undefined {
    return presetId && this.presets.some((preset) => preset.id === presetId) ? presetId : undefined;
  }

  private validLaunchModeId(
    preset: RemoteAgentPresetSummary | undefined,
    launchModeId: string | null | undefined
  ): string | null | undefined {
    if (launchModeId === null) return null;
    if (!preset || !launchModeId) return undefined;
    return preset.launchModes.some((mode) => mode.id === launchModeId) ? launchModeId : undefined;
  }

  private persistLaunchPreferences(): void {
    const prefs: LaunchPreferences = {
      ...(this.els.projectSelect.value ? { projectId: this.els.projectSelect.value } : {}),
      ...(this.els.presetSelect.value ? { presetId: this.els.presetSelect.value } : {}),
      launchModeId: selectValueToLaunchModeId(this.els.launchModeSelect.value),
    };
    this.launchPreferences = prefs;
    writeLaunchPreferences(prefs);
  }

  private setCreateWorkspacePending(pending: boolean): void {
    this.els.createWorkspace.disabled = pending;
    this.els.createWorkspace.classList.toggle('pending', pending);
    this.els.createWorkspace.textContent = pending ? 'Launching...' : 'Launch workspace';
  }

  private terminalIdForWorkspace(workspace: RemoteWorkspaceSummary): string | undefined {
    const primary = workspace.primaryTerminalId
      ? workspace.terminals.find((terminal) => terminal.id === workspace.primaryTerminalId)
      : undefined;
    if (primary && primary.status !== 'stopped') return primary.id;
    return (
      workspace.terminals.find((terminal) => terminal.status === 'running')?.id ??
      workspace.terminals.find((terminal) => terminal.status === 'starting')?.id
    );
  }

  private attachableTerminals(workspace: RemoteWorkspaceSummary): RemoteTerminalSummary[] {
    return workspace.terminals.filter((terminal) => terminal.status !== 'stopped');
  }

  private renderTerminalToolbar(): void {
    const workspace = this.selectedWorkspaceId
      ? this.workspaces.find((candidate) => candidate.id === this.selectedWorkspaceId)
      : undefined;
    const terminals = workspace ? this.attachableTerminals(workspace) : [];
    const selectedTerminalId = workspace
      ? (this.attachedTerminalId ?? this.terminalIdForWorkspace(workspace))
      : undefined;
    const showToolbar = Boolean(workspace && terminals.length > 0);
    this.els.terminalToolbar.hidden = !showToolbar;
    this.els.terminalPanel.classList.toggle('has-terminal-toolbar', showToolbar);
    if (!workspace || terminals.length === 0) {
      this.els.terminalSelect.replaceChildren();
      return;
    }
    this.els.terminalSelect.replaceChildren(
      ...terminals.map((terminal, index) =>
        option(
          terminal.id,
          this.terminalLabel(terminal, index),
          selectedTerminalId ?? terminals[0]?.id ?? ''
        )
      )
    );
    const canCycle = terminals.length > 1 && !this.pendingAttachWorkspaceId;
    this.els.terminalPrev.disabled = !canCycle;
    this.els.terminalNext.disabled = !canCycle;
    this.els.terminalSelect.disabled = Boolean(this.pendingAttachWorkspaceId);
  }

  private terminalLabel(terminal: RemoteTerminalSummary, index: number): string {
    const status = terminal.status === 'running' ? '' : ` (${terminal.status})`;
    return `${terminal.name || `Terminal ${index + 1}`}${status}`;
  }

  private cycleTerminal(delta: -1 | 1): void {
    if (!this.selectedWorkspaceId || this.pendingAttachWorkspaceId) return;
    const workspace = this.workspaces.find((candidate) => candidate.id === this.selectedWorkspaceId);
    if (!workspace) return;
    const terminals = this.attachableTerminals(workspace);
    if (terminals.length < 2) return;
    const currentIndex = Math.max(
      0,
      terminals.findIndex((terminal) => terminal.id === this.attachedTerminalId)
    );
    const nextIndex = (currentIndex + delta + terminals.length) % terminals.length;
    this.attachWorkspace(workspace.id, terminals[nextIndex]!.id);
  }

  private waitForWorkspace(workspaceId: string): void {
    const workspace = this.workspaces.find((candidate) => candidate.id === workspaceId);
    if (!workspace) return;
    this.selectedWorkspaceId = workspaceId;
    this.attachedTerminalId = null;
    this.pendingAttachWorkspaceId = null;
    const status = workspace.provisioning ? 'Preparing workspace...' : 'Waiting for terminal...';
    this.prepareTerminalTransition(status);
    this.renderWorkspaces();
    this.renderTerminalToolbar();
    this.scheduleWorkspaceRefresh();
  }

  private prepareTerminalTransition(status: string): void {
    this.cancelVoiceRecording();
    this.attachedTerminalId = null;
    this.lastSentTerminalSize = null;
    this.lastTerminalSeq = -1;
    this.term.reset();
    this.setTerminalLoading(status);
    this.fitAndSync();
  }

  private setTerminalLoading(status: string): void {
    this.els.terminalStatusText.textContent = status;
    this.els.terminalStatusOverlay.hidden = false;
    this.els.terminalPanel.classList.add('terminal-loading');
  }

  private clearTerminalLoading(): void {
    this.els.terminalStatusOverlay.hidden = true;
    this.els.terminalPanel.classList.remove('terminal-loading');
  }

  private scheduleWorkspaceRefresh(): void {
    if (this.workspaceRefreshTimer !== null) return;
    this.workspaceRefreshTimer = window.setTimeout(() => {
      this.workspaceRefreshTimer = null;
      this.listWorkspaces();
    }, 1000);
  }

  private clearWorkspaceRefreshTimer(): void {
    if (this.workspaceRefreshTimer === null) return;
    window.clearTimeout(this.workspaceRefreshTimer);
    this.workspaceRefreshTimer = null;
  }

  private withPendingCreate(workspaces: RemoteWorkspaceSummary[]): RemoteWorkspaceSummary[] {
    const remoteWorkspaces = sortWorkspaces(workspaces);
    if (!this.pendingCreateWorkspace) return remoteWorkspaces;
    if (remoteWorkspaces.some((workspace) => workspace.id === this.pendingCreateWorkspace?.id)) {
      return remoteWorkspaces;
    }
    return sortWorkspaces([this.pendingCreateWorkspace, ...remoteWorkspaces]);
  }

  private pendingMessageForWorkspace(workspace: RemoteWorkspaceSummary): string {
    if (workspace.id === this.pendingCreateWorkspace?.id) return 'Launching workspace...';
    const explicit = this.pendingWorkspaceMessages.get(workspace.id);
    if (explicit) return explicit;
    if (workspace.provisioning) return 'Preparing workspace...';
    return '';
  }

  private clearWorkspaceAttentionLocal(workspaceId: string, terminalId?: string): void {
    this.workspaces = this.workspaces.map((workspace) => {
      if (workspace.id !== workspaceId) return workspace;
      const terminals = terminalId
        ? workspace.terminals.map((terminal) => {
            if (terminal.id !== terminalId) return terminal;
            const { attention: _attention, ...rest } = terminal;
            return rest;
          })
        : workspace.terminals.map((terminal) => {
            const { attention: _attention, ...rest } = terminal;
            return rest;
          });
      const { attention: _attention, needsAttention: _needsAttention, ...rest } = workspace;
      return {
        ...rest,
        needsAttention: false,
        terminals,
      };
    });
  }

  private updateTerminalStatus(
    workspaceId: string | undefined,
    terminalId: string,
    status: RemoteWorkspaceSummary['terminals'][number]['status']
  ): void {
    this.workspaces = this.workspaces.map((workspace) =>
      (
        workspaceId
          ? workspace.id === workspaceId
          : workspace.terminals.some((terminal) => terminal.id === terminalId)
      )
        ? {
            ...workspace,
            terminals: workspace.terminals.map((terminal) =>
              terminal.id === terminalId ? { ...terminal, status } : terminal
            ),
          }
        : workspace
    );
    if (terminalId !== this.attachedTerminalId || status !== 'stopped') {
      this.renderWorkspaces();
      this.renderTerminalToolbar();
      return;
    }
    this.cancelVoiceRecording();
    this.attachedTerminalId = null;
    this.pendingAttachWorkspaceId = null;
    this.clearTerminalLoading();
    this.term.writeln('\r\n\r\nTerminal stopped.');
    this.renderWorkspaces();
    this.renderTerminalToolbar();
    this.updateVoiceUi();
  }

  private clearPendingRequest(requestId: string | undefined): void {
    if (!requestId) return;
    const timer = this.pendingRequestTimers.get(requestId);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      this.pendingRequestTimers.delete(requestId);
    }
    const workspaceId = this.pendingRequestWorkspaces.get(requestId);
    if (!workspaceId) return;
    this.pendingRequestWorkspaces.delete(requestId);
    this.pendingWorkspaceMessages.delete(workspaceId);
  }

  private trackPendingRequest(
    requestId: string,
    workspaceId: string,
    timeoutMessage: string
  ): void {
    this.pendingRequestWorkspaces.set(requestId, workspaceId);
    const timer = window.setTimeout(() => {
      this.pendingRequestTimers.delete(requestId);
      if (this.pendingRequestWorkspaces.get(requestId) !== workspaceId) return;
      this.pendingRequestWorkspaces.delete(requestId);
      if (this.pendingCreateRequestId === requestId) {
        this.pendingCreateRequestId = null;
        this.pendingCreateWorkspace = null;
      }
      this.pendingWorkspaceMessages.set(workspaceId, timeoutMessage);
      if (this.pendingAttachWorkspaceId === workspaceId) {
        this.pendingAttachWorkspaceId = null;
        this.setTerminalLoading(timeoutMessage);
      }
      this.setCreateWorkspacePending(false);
      this.renderWorkspaces();
      this.renderTerminalToolbar();
    }, REMOTE_REQUEST_TIMEOUT_MS);
    this.pendingRequestTimers.set(requestId, timer);
  }

  private fitAfterLayout(): void {
    requestAnimationFrame(() => {
      this.fitAndSync();
      requestAnimationFrame(() => this.fitAndSync());
    });
  }

  private showWorkspaceList(): void {
    this.cancelVoiceRecording();
    this.els.shell.classList.add('workspace-view');
    this.els.shell.classList.remove('terminal-view');
  }

  private showTerminal(): void {
    this.els.shell.classList.add('terminal-view');
    this.els.shell.classList.remove('workspace-view');
    this.updateVoiceUi();
    this.updateVisualViewportSize();
    this.fitAfterLayout();
  }

  private fitAndSync(): { cols: number; rows: number } | undefined {
    try {
      this.fitAddon.fit();
      const dimensions = this.fitAddon.proposeDimensions();
      if (!dimensions) return undefined;
      const sizeKey = this.sizeKey(dimensions);
      if (this.attachedTerminalId && sizeKey !== this.lastSentTerminalSize) {
        this.lastSentTerminalSize = sizeKey;
        this.sendRemoteFrame({
          type: 'app:terminal:resize',
          terminalId: this.attachedTerminalId,
          cols: dimensions.cols,
          rows: dimensions.rows,
        });
      }
      return dimensions;
    } catch {
      // xterm can reject fit while hidden during startup.
      return undefined;
    }
  }

  private sizeKey(dimensions: { cols: number; rows: number }): string {
    return `${dimensions.cols}x${dimensions.rows}`;
  }

  private sendTerminalInput(data: string): void {
    if (!this.attachedTerminalId) return;
    this.sendRemoteFrame({
      type: 'app:terminal:input',
      terminalId: this.attachedTerminalId,
      data,
    });
  }

  // ── Voice dictation ─────────────────────────────────────────────────
  // Records mic audio in the browser, ships 16 kHz Int16 PCM to the desktop,
  // which transcribes locally and types the text into the attached terminal.

  private bindVoiceControls(): void {
    this.els.voiceRecord.addEventListener('click', () => void this.startVoiceRecording());
    this.els.voiceEnter.addEventListener('click', () => this.sendTerminalInput('\r'));
    this.els.voiceAbort.addEventListener('click', () => this.cancelVoiceRecording());
    this.els.voiceSend.addEventListener('click', () => this.stopAndSendVoice(false));
    this.els.voiceSendEnter.addEventListener('click', () => this.stopAndSendVoice(true));
  }

  private get isRecordingVoice(): boolean {
    return this.voiceCapture !== null;
  }

  private async startVoiceRecording(): Promise<void> {
    if (!this.voiceAvailable || this.voiceStarting || this.isRecordingVoice) return;
    if (!this.attachedTerminalId) {
      this.setVoiceStatus('Open a terminal first', 2500);
      return;
    }
    this.voiceTarget = 'terminal';
    this.voiceStarting = true;
    this.updateVoiceUi();
    this.queryVoiceAvailability();
    try {
      this.voiceCapture = await startAudioCapture();
    } catch (err) {
      console.warn('Mic capture failed', err);
      const detail = err instanceof Error && err.name ? err.name : 'unavailable';
      this.setVoiceStatus(`Mic ${detail}`, 4000);
      this.voiceStarting = false;
      this.voiceTarget = null;
      this.updateVoiceUi();
      this.updateWorkspaceVoiceUi();
      return;
    }
    this.voiceStarting = false;
    this.setVoiceStatus('', 0);
    this.voiceLevelTimer = window.setInterval(() => {
      const level = this.voiceCapture?.getLevel() ?? 0;
      const text = `● ${'▁▂▃▄▅▆▇█'[Math.min(7, Math.round(level * 14))]}`;
      this.els.voiceMeter.textContent = text;
      this.els.workspaceVoiceMeter.textContent = text;
    }, 100);
    this.updateVoiceUi();
    this.updateWorkspaceVoiceUi();
  }

  private async startWorkspaceVoice(): Promise<void> {
    if (!this.voiceAvailable || this.voiceStarting || this.isRecordingVoice) return;
    if (this.projects.length === 0 || this.presets.length === 0) this.getLaunchOptions();
    this.voiceTarget = 'workspace';
    this.voiceStarting = true;
    this.setWorkspaceVoiceStatus('Starting mic');
    this.updateWorkspaceVoiceUi();
    this.queryVoiceAvailability();
    try {
      this.voiceCapture = await startAudioCapture();
    } catch (err) {
      console.warn('Mic capture failed', err);
      const detail = err instanceof Error && err.name ? err.name : 'unavailable';
      this.setWorkspaceVoiceStatus(`Mic ${detail}`);
      this.voiceStarting = false;
      this.voiceTarget = null;
      this.updateWorkspaceVoiceUi();
      return;
    }
    this.voiceStarting = false;
    this.setWorkspaceVoiceStatus('Recording');
    this.voiceLevelTimer = window.setInterval(() => {
      const level = this.voiceCapture?.getLevel() ?? 0;
      const text = `● ${'▁▂▃▄▅▆▇█'[Math.min(7, Math.round(level * 14))]}`;
      this.els.voiceMeter.textContent = text;
      this.els.workspaceVoiceMeter.textContent = text;
    }, 100);
    this.updateWorkspaceVoiceUi();
  }

  // Stop recording and ship the audio. `enter` controls whether the desktop
  // submits the transcript with a trailing carriage return.
  private stopAndSendVoice(enter: boolean): void {
    const capture = this.voiceCapture;
    const terminalId = this.attachedTerminalId;
    this.clearVoiceLevelTimer();
    this.voiceCapture = null;
    this.voiceTarget = null;
    if (!capture) {
      this.updateVoiceUi();
      return;
    }
    const samples = capture.stop();
    if (!terminalId || samples.length === 0) {
      this.setVoiceStatus('No audio', 2000);
      this.updateVoiceUi();
      return;
    }
    const requestId = this.requestId();
    this.pendingVoiceRequestId = requestId;
    this.setVoiceStatus('Transcribing…');
    this.updateVoiceUi();
    const sent = this.sendVoiceChunks(requestId, terminalId, floatToPcm16(samples), enter);
    if (!sent) {
      this.pendingVoiceRequestId = null;
      this.setVoiceStatus('', 0);
    } else {
      // The desktop types the result straight into the terminal; clear the
      // pending marker after a while so the status doesn't linger forever.
      window.setTimeout(() => {
        if (this.pendingVoiceRequestId === requestId) {
          this.pendingVoiceRequestId = null;
          this.setVoiceStatus('', 0);
        }
      }, 6000);
    }
  }

  private stopAndUseWorkspaceVoice(action: 'launch' | 'edit'): void {
    const capture = this.voiceCapture;
    this.clearVoiceLevelTimer();
    this.voiceCapture = null;
    this.voiceTarget = null;
    if (!capture) {
      this.updateWorkspaceVoiceUi();
      return;
    }
    const samples = capture.stop();
    if (samples.length === 0) {
      this.setWorkspaceVoiceStatus('No audio');
      this.updateWorkspaceVoiceUi();
      return;
    }
    const requestId = this.requestId();
    this.pendingVoiceRequestId = requestId;
    this.pendingWorkspaceVoiceAction = action;
    this.setWorkspaceVoiceStatus(action === 'edit' ? 'Transcribing for edit...' : 'Transcribing...');
    this.updateWorkspaceVoiceUi();
    const sent = this.sendVoiceChunks(requestId, undefined, floatToPcm16(samples), false, 'prompt');
    if (!sent) {
      this.pendingVoiceRequestId = null;
      this.pendingWorkspaceVoiceAction = null;
      this.setWorkspaceVoiceStatus('');
      this.updateWorkspaceVoiceUi();
    }
  }

  private handleWorkspaceVoiceTranscript(requestId: string, text: string): void {
    if (requestId !== this.pendingVoiceRequestId || !this.pendingWorkspaceVoiceAction) return;
    const action = this.pendingWorkspaceVoiceAction;
    this.pendingVoiceRequestId = null;
    this.pendingWorkspaceVoiceAction = null;
    this.setWorkspaceVoiceStatus('');
    this.updateWorkspaceVoiceUi();
    const prompt = text.trim();
    if (action === 'edit' || !prompt) {
      this.openCreateDialog();
      this.els.initialPrompt.value = prompt;
      this.els.initialPrompt.focus();
      return;
    }
    this.createWorkspace(prompt);
  }

  // Split the PCM into ordered chunks small enough to clear the relay's ~1 MiB
  // WebSocket message cap, then send them as one reassembled recording. 192 KiB
  // of PCM base64-encodes to ~256 KiB. Boundaries stay 2-byte (Int16) aligned.
  private sendVoiceChunks(
    requestId: string,
    terminalId: string | undefined,
    pcm: Uint8Array,
    enter: boolean,
    purpose: 'terminal' | 'prompt' = 'terminal'
  ): boolean {
    // Each chunk is base64'd into the `audio` field, then the whole app frame
    // is JSON-encoded, encrypted, and base64'd again into the `e2e:enc`
    // envelope. The receiving Peer rejects any wire frame over MAX_FRAME_CHARS
    // (64 KiB), so the chunk must be small enough that the doubly-base64'd
    // envelope stays under that. 32 KiB of PCM lands the envelope around
    // 58 KiB, leaving headroom; 192 KiB blew straight past the cap and made
    // every recording disconnect.
    const CHUNK_BYTES = 32 * 1024;
    const chunkCount = Math.max(1, Math.ceil(pcm.length / CHUNK_BYTES));
    for (let seq = 0; seq < chunkCount; seq++) {
      const start = seq * CHUNK_BYTES;
      const slice = pcm.subarray(start, Math.min(start + CHUNK_BYTES, pcm.length));
      const sent = this.sendRemoteFrame({
        type: 'app:voice:transcribe',
        requestId,
        ...(terminalId ? { terminalId } : {}),
        ...(purpose === 'prompt' ? { purpose } : {}),
        audio: encodeBase64(slice),
        seq,
        final: seq === chunkCount - 1,
        enter,
      });
      if (!sent) return false;
    }
    return true;
  }

  private cancelVoiceRecording(): void {
    this.clearVoiceLevelTimer();
    this.voiceCapture?.cancel();
    this.voiceCapture = null;
    this.voiceTarget = null;
    this.pendingWorkspaceVoiceAction = null;
    this.setVoiceStatus('', 0);
    this.setWorkspaceVoiceStatus('');
    this.updateVoiceUi();
    this.updateWorkspaceVoiceUi();
  }

  private clearVoiceLevelTimer(): void {
    if (this.voiceLevelTimer !== null) {
      window.clearInterval(this.voiceLevelTimer);
      this.voiceLevelTimer = null;
    }
  }

  private setVoiceStatus(text: string, clearAfterMs = 0): void {
    if (this.voiceStatusTimer !== null) {
      window.clearTimeout(this.voiceStatusTimer);
      this.voiceStatusTimer = null;
    }
    this.els.voiceStatus.textContent = text;
    this.els.voiceStatus.hidden = text.length === 0;
    if (text.length > 0 && clearAfterMs > 0) {
      this.voiceStatusTimer = window.setTimeout(() => {
        this.voiceStatusTimer = null;
        this.els.voiceStatus.textContent = '';
        this.els.voiceStatus.hidden = true;
      }, clearAfterMs);
    }
  }

  private updateVoiceUi(): void {
    const voiceReady = this.voiceAvailable && this.attachedTerminalId !== null;
    const terminalRecording = this.isRecordingVoice && this.voiceTarget === 'terminal';
    // Voice bar when the keyboard is down; terminal key shortcuts when it's up.
    // Falls back to the key shortcuts when voice isn't available at all. While
    // a recording is in flight the voice bar stays up so Send/Abort stay
    // reachable even if the keyboard happens to open.
    const showVoiceBar = voiceReady && (!this.keyboardOpen || terminalRecording);
    const panel = this.els.terminalPanel.classList;
    panel.toggle('bar-voice', showVoiceBar);
    panel.toggle('bar-keys', !showVoiceBar);
    this.els.voiceBar.hidden = !showVoiceBar;
    if (!showVoiceBar) return;
    const recording = terminalRecording;
    this.els.voiceIdle.hidden = recording;
    this.els.voiceLive.hidden = !recording;
    this.els.voiceRecord.disabled = this.voiceStarting;
  }

  private setWorkspaceVoiceStatus(text: string): void {
    this.els.workspaceVoiceStatus.textContent = text;
  }

  private updateWorkspaceVoiceUi(): void {
    const recording = this.isRecordingVoice && this.voiceTarget === 'workspace';
    const pending = this.pendingWorkspaceVoiceAction !== null;
    this.els.voiceCreateWorkspace.hidden = !this.voiceAvailable;
    this.els.voiceCreateWorkspace.disabled = this.voiceStarting || this.isRecordingVoice || pending;
    this.els.workspaceVoice.hidden = !(recording || pending);
    this.els.workspaceVoice.classList.toggle('pending', pending);
    this.els.workspaceVoiceLaunch.disabled = pending;
    this.els.workspaceVoiceEdit.disabled = pending;
    this.els.workspaceVoiceCancel.disabled = pending;
    if (!recording && !pending) {
      this.els.workspaceVoiceMeter.textContent = '●';
    } else if (pending) {
      this.els.workspaceVoiceMeter.textContent = '';
    }
  }

  private configureMobileTerminalInput(): void {
    const textarea = this.term.textarea;
    if (!textarea) return;
    textarea.autocomplete = 'off';
    textarea.autocapitalize = 'off';
    textarea.spellcheck = false;
    textarea.inputMode = 'text';
    textarea.enterKeyHint = 'enter';
    textarea.setAttribute('autocorrect', 'off');
  }

  private bindMobileKeyboardBar(): void {
    for (const button of this.els.mobileKeyboardBar.querySelectorAll<HTMLButtonElement>(
      'button[data-terminal-key]'
    )) {
      const data = MOBILE_TERMINAL_KEYS[button.dataset.terminalKey ?? ''] ?? '';
      button.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        this.term.focus();
        this.sendTerminalInput(data);
      });
      button.addEventListener('click', (event) => event.preventDefault());
    }
  }

  private bindVisualViewport(): void {
    this.updateVisualViewportSize();
    window.visualViewport?.addEventListener('resize', () => this.updateVisualViewportSize());
    window.visualViewport?.addEventListener('scroll', () => this.updateVisualViewportSize());
    window.addEventListener('orientationchange', () => this.fitAfterLayout());
  }

  private updateVisualViewportSize(): void {
    const viewport = window.visualViewport;
    const height = viewport?.height ?? window.innerHeight;
    const offsetTop = viewport?.offsetTop ?? 0;
    document.documentElement.style.setProperty('--remote-viewport-height', `${height}px`);
    document.documentElement.style.setProperty('--remote-viewport-top', `${offsetTop}px`);
    // The soft keyboard shrinks the visual viewport well below the layout
    // height. When it's up we surface the terminal key shortcuts; when it's
    // down we show the voice bar instead.
    const keyboardOpen = window.innerHeight - height > 150;
    if (keyboardOpen !== this.keyboardOpen) {
      this.keyboardOpen = keyboardOpen;
      this.updateVoiceUi();
    }
    this.fitAfterLayout();
  }

  private ensureConnected(): boolean {
    if (!this.disconnected && this.peer.state === 'established') return true;
    this.markDisconnected();
    return false;
  }

  private sendRemoteFrame(
    frame: RemoteAppFrame,
    pending?: { requestId?: string; workspaceId?: string }
  ): boolean {
    if (!this.ensureConnected()) {
      this.clearFailedSend(pending);
      return false;
    }
    void this.peer.sendEncryptedJson(frame).catch((err) => {
      console.warn('Remote send failed', err);
      this.clearFailedSend(pending);
      this.markDisconnected();
    });
    return true;
  }

  private clearFailedSend(pending?: { requestId?: string; workspaceId?: string }): void {
    if (pending?.requestId) {
      const timer = this.pendingRequestTimers.get(pending.requestId);
      if (timer !== undefined) window.clearTimeout(timer);
      this.pendingRequestTimers.delete(pending.requestId);
      this.pendingRequestWorkspaces.delete(pending.requestId);
    }
    if (pending?.requestId === this.pendingCreateRequestId) {
      this.pendingCreateRequestId = null;
      this.pendingCreateWorkspace = null;
    }
    if (pending?.workspaceId) this.pendingWorkspaceMessages.delete(pending.workspaceId);
    this.pendingAttachWorkspaceId = null;
    this.setCreateWorkspacePending(false);
    this.clearTerminalLoading();
    this.renderWorkspaces();
    this.renderTerminalToolbar();
  }

  private markDisconnected(): void {
    if (this.disconnected) return;
    this.disconnected = true;
    this.cancelVoiceRecording();
    this.clearWorkspaceRefreshTimer();
    this.pendingAttachWorkspaceId = null;
    this.pendingCreateRequestId = null;
    this.pendingCreateWorkspace = null;
    for (const timer of this.pendingRequestTimers.values()) {
      window.clearTimeout(timer);
    }
    this.pendingRequestTimers.clear();
    this.pendingRequestWorkspaces.clear();
    this.pendingWorkspaceMessages.clear();
    this.setCreateWorkspacePending(false);
    this.clearTerminalLoading();
    const reconnectDelay = nextPageReconnectDelay();
    this.term.writeln(
      `\r\n\r\nDisconnected. Reconnecting in ${Math.round(reconnectDelay / 1000)}s...`
    );
    this.renderWorkspaces();
    this.renderTerminalToolbar();
    window.setTimeout(() => location.reload(), reconnectDelay);
  }

}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}

function option(value: string, label: string, selectedValue: string): HTMLOptionElement {
  const el = document.createElement('option');
  el.value = value;
  el.textContent = label;
  el.selected = value === selectedValue;
  return el;
}

function selectValueToLaunchModeId(value: string): string | null {
  return value ? value : null;
}

function launchModeIdToSelectValue(value: string | null | undefined): string {
  return value ?? '';
}

function readLaunchPreferences(): LaunchPreferences {
  try {
    const raw = localStorage.getItem(REMOTE_LAUNCH_PREFERENCES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<LaunchPreferences>;
    return {
      ...(typeof parsed.projectId === 'string' ? { projectId: parsed.projectId } : {}),
      ...(typeof parsed.presetId === 'string' ? { presetId: parsed.presetId } : {}),
      ...(typeof parsed.launchModeId === 'string' || parsed.launchModeId === null
        ? { launchModeId: parsed.launchModeId }
        : {}),
    };
  } catch {
    return {};
  }
}

function writeLaunchPreferences(preferences: LaunchPreferences): void {
  try {
    localStorage.setItem(REMOTE_LAUNCH_PREFERENCES_KEY, JSON.stringify(preferences));
  } catch {
    // best-effort
  }
}

function sortWorkspaces(workspaces: RemoteWorkspaceSummary[]): RemoteWorkspaceSummary[] {
  return [...workspaces].sort(
    (a, b) => (b.lastActivityAt ?? b.createdAt ?? 0) - (a.lastActivityAt ?? a.createdAt ?? 0)
  );
}

function resetPageReconnectAttempts(): void {
  try {
    sessionStorage.removeItem(REMOTE_RECONNECT_ATTEMPT_KEY);
  } catch {
    // best-effort
  }
}

function nextPageReconnectDelay(): number {
  try {
    const attempt = Number.parseInt(
      sessionStorage.getItem(REMOTE_RECONNECT_ATTEMPT_KEY) ?? '0',
      10
    );
    const safeAttempt = Number.isFinite(attempt) && attempt > 0 ? attempt : 0;
    sessionStorage.setItem(REMOTE_RECONNECT_ATTEMPT_KEY, String(safeAttempt + 1));
    return Math.min(
      REMOTE_RECONNECT_MAX_DELAY_MS,
      REMOTE_RECONNECT_BASE_DELAY_MS * 2 ** safeAttempt
    );
  } catch {
    return REMOTE_RECONNECT_BASE_DELAY_MS;
  }
}

function scheduleAuthReconnect(els: UI): void {
  const reconnectDelay = nextPageReconnectDelay();
  setStatus(els.status, 'connecting', `Reconnecting in ${Math.round(reconnectDelay / 1000)}s…`);
  window.setTimeout(() => location.reload(), reconnectDelay);
}

function formatRelativeTime(timestamp: number): string {
  const diff = Math.max(0, Date.now() - timestamp);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < minute) return 'now';
  if (diff < hour) return `${Math.floor(diff / minute)}m`;
  if (diff < day) return `${Math.floor(diff / hour)}h`;
  return `${Math.floor(diff / day)}d`;
}

type RemoteWorkspaceAttention = NonNullable<RemoteWorkspaceSummary['attention']>;

function attentionLabel(kind: RemoteWorkspaceAttention['kind']): string {
  switch (kind) {
    case 'input-required':
      return 'Input';
    case 'turn-complete':
      return 'Done';
    case 'error':
      return 'Error';
    case 'idle':
      return 'Idle';
    default:
      return 'Notice';
  }
}

function attentionClassName(kind: RemoteWorkspaceAttention['kind']): string {
  switch (kind) {
    case 'input-required':
      return 'attention-input';
    case 'turn-complete':
      return 'attention-done';
    case 'error':
      return 'attention-error';
    case 'idle':
      return 'attention-idle';
    default:
      return 'attention-generic';
  }
}

// ────────────────────────────────────────────────────────────────────────
// Entry — dispatch on URL fragment.
// ────────────────────────────────────────────────────────────────────────

async function start(): Promise<void> {
  const els = ui();
  const hash = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash;
  const hasFragment = hash.length > 0;
  if (hasFragment) {
    await runRegisterFlow(els);
    return;
  }
  await runAuthFlow(els);
}

// ────────────────────────────────────────────────────────────────────────
// Registration flow (URL has `#fp=…`)
// ────────────────────────────────────────────────────────────────────────

async function runRegisterFlow(els: UI): Promise<void> {
  els.title.textContent = 'Baton — pair a new device';
  els.subtitle.textContent =
    'Register a passkey for this Baton install. The relay only forwards bytes — your passkey material never leaves this device.';
  els.fingerprintRow.hidden = false;
  els.label.value = deriveDefaultLabel();

  let parsed: ReturnType<typeof parsePairUrl>;
  try {
    parsed = parsePairUrl(location.href);
  } catch (err) {
    if (err instanceof PairUrlError) {
      setStatus(els.status, 'error', 'Bad pair URL');
      showError(els, err.message);
    } else {
      setStatus(els.status, 'error', 'Bad URL');
      showError(els, (err as Error).message);
    }
    return;
  }

  els.fingerprint.textContent = shortFp(parsed.fingerprint);
  els.fingerprint.title = parsed.fingerprint;
  const directMode = isDirectMode();
  setStatus(
    els.status,
    'connecting',
    directMode ? 'Connecting to desktop…' : 'Connecting to relay…'
  );

  const ws = new WebSocket(socketUrlForFingerprint(parsed.fingerprint, parsed));

  const peer = new Peer({
    role: 'client',
    expectedFingerprint: parsed.fingerprint,
    transport: {
      send: (data) => ws.send(data),
      close: (code, reason) => ws.close(code, reason),
    },
  });

  let pendingChallenge: PairRegistrationChallenge | null = null;
  let terminalFrameProcessed = false;

  peer.onMessage((msg) => {
    if (msg.kind !== 'json') {
      showError(els, 'Desktop sent an unexpected binary frame.');
      return;
    }
    const frame = parsePairFrame(msg.data);
    if (!frame) {
      if (!terminalFrameProcessed) showError(els, 'Desktop sent an unrecognised frame.');
      return;
    }
    void handleFrame(frame);
  });

  async function handleFrame(frame: PairFrame): Promise<void> {
    if (frame.type === 'pair:registration-challenge') {
      pendingChallenge = frame;
      try {
        await runRegistration(peer, frame, els, parsed.fingerprint);
      } catch (err) {
        showError(els, (err as Error).message);
        setStatus(els.status, 'error', 'Pair failed');
        ws.close(1000, 'browser-aborted');
      }
      return;
    }
    if (frame.type === 'pair:registered') {
      terminalFrameProcessed = true;
      setStatus(els.status, 'registered', 'Paired');
      els.form.hidden = true;
      els.register.hidden = true;
      // Cache fingerprint so subsequent reconnects skip the discovery prompt.
      try {
        localStorage.setItem(FP_CACHE_KEY, parsed.fingerprint);
      } catch {
        // localStorage may be disabled in private mode — that just costs an
        // extra prompt next time, not correctness.
      }
      if (directMode) {
        showSuccess(
          els,
          `${frame.label} is paired. Continue with your passkey to open Baton Remote.`
        );
        ws.close(1000, 'ceremony-complete');
        location.assign(`${location.origin}/remote/`);
      } else {
        notifyNativeSessionReady(frame.label);
        new RemoteAppClient(peer).start(frame.label);
      }
      return;
    }
    if (frame.type === 'pair:rejected') {
      terminalFrameProcessed = true;
      setStatus(els.status, 'error', `Pair rejected (${frame.reason})`);
      showError(els, frame.message);
      ws.close(1000, 'ceremony-complete');
      return;
    }
    showError(els, `Unexpected frame from desktop: ${frame.type}`);
  }

  ws.addEventListener('message', (evt) => {
    handleSocketMessage(evt, ws, peer);
  });

  ws.addEventListener('close', (evt) => {
    const wasReady = peer.state === 'established';
    peer.handleClose({ code: evt.code, reason: evt.reason });
    const reason = evt.reason ?? '';
    if (terminalFrameProcessed || reason === 'browser-aborted') return;
    if (wasReady && (evt.code === 1000 || evt.code === 1001)) {
      setStatus(els.status, 'idle', 'Disconnected');
    } else {
      setStatus(els.status, 'error', 'Disconnected');
      showError(els, reason ? `Closed: ${evt.code} ${reason}` : `Closed: ${evt.code}`);
    }
    els.register.disabled = true;
  });

  ws.addEventListener('error', () => {
    peer.handleError(new Error('socket error'));
  });

  peer.onEstablished(() => {
    setStatus(els.status, 'ready', 'Ready to pair');
    els.form.hidden = false;
    els.register.hidden = false;
    els.register.disabled = false;
    els.code.focus();
  });

  ws.addEventListener('open', () => {
    void peer.connect().catch((err) => {
      setStatus(els.status, 'error', 'Handshake failed');
      showError(els, (err as Error).message);
    });
  });

  els.register.addEventListener('click', () => {
    if (peer.state !== 'established') return;
    if (pendingChallenge) return; // double-click
    const code = els.code.value.trim();
    const labelText = els.label.value.trim();
    if (!/^\d{6}$/.test(code)) {
      showError(els, 'Pairing code must be 6 digits.');
      els.code.focus();
      return;
    }
    if (labelText.length === 0) {
      showError(els, 'Device name is required.');
      els.label.focus();
      return;
    }
    els.register.disabled = true;
    els.error.hidden = true;
    setStatus(els.status, 'verifying', 'Sending pair request…');
    void peer
      .sendEncryptedJson({
        type: 'pair:hello',
        protocolVersion: PAIR_PROTOCOL_VERSION,
        mode: 'register',
        pairingCode: code,
        proposedLabel: labelText,
      })
      .catch((err) => {
        showError(els, `Send failed: ${(err as Error).message}`);
        els.register.disabled = false;
      });
  });
}

async function runRegistration(
  peer: Peer,
  challenge: PairRegistrationChallenge,
  els: UI,
  expectedFingerprint: string
): Promise<void> {
  if (typeof window.PublicKeyCredential === 'undefined' || !navigator.credentials?.create) {
    throw new Error("This browser doesn't support WebAuthn passkeys.");
  }
  if (challenge.origin !== location.origin) {
    throw new Error(
      `Pair URL origin mismatch (page is ${location.origin}, desktop expected ${challenge.origin}).`
    );
  }
  // The desktop sets `user.id` to base64url(install-fingerprint-hex). We
  // recover that fingerprint from the WebAuthn assertion's `userHandle` on
  // every later reconnect, so the credential we're about to create here MUST
  // be bound to the fingerprint the user thinks they're pairing with — i.e.
  // the one in the URL fragment we landed on. Without this check, a buggy
  // or misrouted desktop could mint a passkey whose discoverable userHandle
  // points at a different install, and reconnect would silently break once
  // the localStorage cache was cleared.
  const userIdBytes = base64UrlToBytes(challenge.user.id);
  const fingerprintInChallenge = userHandleToFingerprint(userIdBytes);
  if (fingerprintInChallenge !== expectedFingerprint) {
    throw new Error(
      'Desktop sent a registration challenge for a different install. Refresh the pair URL and try again.'
    );
  }
  const publicKey: PublicKeyCredentialCreationOptions = {
    challenge: base64UrlToBytes(challenge.challenge),
    rp: { id: challenge.rpId, name: challenge.rpName },
    user: {
      id: userIdBytes,
      name: challenge.user.name,
      displayName: challenge.user.displayName,
    },
    pubKeyCredParams: [
      { type: 'public-key', alg: -7 },
      { type: 'public-key', alg: -257 },
    ],
    authenticatorSelection: {
      // Reconnect performs a discoverable `credentials.get()` with an empty
      // `allowCredentials` and recovers the install fingerprint from the
      // assertion's `userHandle`. That only works against discoverable
      // credentials — `preferred` would let some authenticators silently
      // create a non-discoverable one, breaking canonical-URL reconnect for
      // anyone whose localStorage cache is gone.
      residentKey: 'required',
      requireResidentKey: true,
      userVerification: 'preferred',
    },
    attestation: 'none',
    excludeCredentials: challenge.excludeCredentialIds.map((id) => ({
      type: 'public-key',
      id: base64UrlToBytes(id),
    })),
    timeout: 60_000,
  };
  setStatus(els.status, 'verifying', 'Waiting for biometric…');
  const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
  if (!cred) throw new Error('Authenticator returned no credential.');
  await peer.sendEncryptedJson({
    type: 'pair:registration-response',
    attestationResponse: attestationToJSON(cred),
  });
  // Caching happens on `pair:registered` — only after the desktop confirms
  // the registration was persisted. Caching here would poison reconnect if
  // the desktop rejects (attestation-invalid, duplicate-credential, etc.).
  setStatus(els.status, 'verifying', 'Verifying with desktop…');
}

// ────────────────────────────────────────────────────────────────────────
// Authenticated reconnect flow (URL has no fragment)
// ────────────────────────────────────────────────────────────────────────

async function runAuthFlow(els: UI): Promise<void> {
  els.title.textContent = 'Baton — reconnect';
  els.subtitle.textContent = 'Connecting to your Baton install with a paired passkey.';
  els.fingerprintRow.hidden = false;
  els.register.hidden = true;
  els.form.hidden = true;

  if (typeof window.PublicKeyCredential === 'undefined' || !navigator.credentials?.get) {
    setStatus(els.status, 'error', 'WebAuthn not available');
    showError(els, "This browser doesn't support WebAuthn passkeys.");
    return;
  }

  setStatus(els.status, 'connecting', 'Looking up paired install…');
  const direct = readDirectRemoteConfig();
  let fingerprint: string | null = direct?.fingerprint ?? null;
  try {
    fingerprint = fingerprint ?? readCachedFingerprint();
  } catch {
    fingerprint = direct?.fingerprint ?? null;
  }

  // Tracks whether `fingerprint` came from the cache or from a fresh
  // `discoverFingerprint()` call. Discovery results aren't trusted enough to
  // cache yet — the user might have picked the wrong Baton passkey, or the
  // desktop might be offline / wiped this device. Wait for `pair:authenticated`
  // before writing it; otherwise a wrong choice lingers in cache and the user
  // can't make progress next time.
  const fingerprintCameFromCache = fingerprint !== null;

  if (!fingerprint) {
    try {
      fingerprint = await discoverFingerprint();
    } catch (err) {
      setStatus(els.status, 'error', 'Discovery failed');
      showError(els, (err as Error).message);
      return;
    }
    if (!fingerprint) {
      setStatus(els.status, 'error', 'No passkey selected');
      showError(
        els,
        'Select a Baton passkey to continue, or pair this device from the desktop first.'
      );
      return;
    }
  }

  els.fingerprint.textContent = shortFp(fingerprint);
  els.fingerprint.title = fingerprint;
  setStatus(els.status, 'connecting', direct ? 'Connecting to desktop…' : 'Connecting to relay…');

  const ws = new WebSocket(socketUrlForFingerprint(fingerprint));

  const peer = new Peer({
    role: 'client',
    expectedFingerprint: fingerprint,
    transport: {
      send: (data) => ws.send(data),
      close: (code, reason) => ws.close(code, reason),
    },
  });

  let terminalFrameProcessed = false;

  peer.onMessage((msg) => {
    if (msg.kind !== 'json') {
      showError(els, 'Desktop sent an unexpected binary frame.');
      return;
    }
    const frame = parsePairFrame(msg.data);
    if (!frame) {
      if (!terminalFrameProcessed) showError(els, 'Desktop sent an unrecognised frame.');
      return;
    }
    void handleAuthFrame(frame);
  });

  async function handleAuthFrame(frame: PairFrame): Promise<void> {
    if (frame.type === 'pair:auth-challenge') {
      try {
        await respondToAuthChallenge(peer, frame, els);
      } catch (err) {
        showError(els, (err as Error).message);
        setStatus(els.status, 'error', 'Auth failed');
        ws.close(1000, 'browser-aborted');
      }
      return;
    }
    if (frame.type === 'pair:authenticated') {
      terminalFrameProcessed = true;
      // Discovery-derived fingerprints are only persisted once the desktop
      // confirms — until now we had no signal that this fingerprint resolves
      // to anything reachable.
      if (!fingerprintCameFromCache && fingerprint) {
        try {
          localStorage.setItem(FP_CACHE_KEY, fingerprint);
        } catch {
          // best-effort
        }
      }
      setStatus(els.status, 'authenticated', 'Connected');
      notifyNativeSessionReady(frame.label);
      new RemoteAppClient(peer).start(frame.label);
      return;
    }
    if (frame.type === 'pair:rejected') {
      terminalFrameProcessed = true;
      setStatus(els.status, 'error', `Auth rejected (${frame.reason})`);
      showError(els, frame.message);
      // unknown-credential is the signal the desktop has wiped this device
      // (or never had it). Drop the cache so the user re-discovers next time.
      if (frame.reason === 'unknown-credential' || frame.reason === 'not-paired') {
        try {
          localStorage.removeItem(FP_CACHE_KEY);
        } catch {
          // best-effort
        }
      }
      ws.close(1000, 'ceremony-complete');
      return;
    }
    showError(els, `Unexpected frame from desktop: ${frame.type}`);
  }

  ws.addEventListener('message', (evt) => {
    handleSocketMessage(evt, ws, peer);
  });

  ws.addEventListener('close', (evt) => {
    const wasReady = peer.state === 'established';
    peer.handleClose({ code: evt.code, reason: evt.reason });
    const reason = evt.reason ?? '';
    if (terminalFrameProcessed || reason === 'browser-aborted') return;
    if (direct) {
      scheduleAuthReconnect(els);
      return;
    }
    if (wasReady && (evt.code === 1000 || evt.code === 1001)) {
      setStatus(els.status, 'idle', 'Disconnected');
    } else {
      setStatus(els.status, 'error', 'Disconnected');
      showError(els, reason ? `Closed: ${evt.code} ${reason}` : `Closed: ${evt.code}`);
    }
  });

  ws.addEventListener('error', () => {
    peer.handleError(new Error('socket error'));
  });

  peer.onEstablished(() => {
    setStatus(els.status, 'verifying', 'Authenticating…');
    void peer
      .sendEncryptedJson({
        type: 'pair:hello',
        protocolVersion: PAIR_PROTOCOL_VERSION,
        mode: 'authenticate',
      })
      .catch((err) => {
        showError(els, `Send failed: ${(err as Error).message}`);
      });
  });

  ws.addEventListener('open', () => {
    void peer.connect().catch((err) => {
      setStatus(els.status, 'error', 'Handshake failed');
      showError(els, (err as Error).message);
    });
  });
}

function readCachedFingerprint(): string | null {
  const raw = localStorage.getItem(FP_CACHE_KEY);
  if (!raw) return null;
  return /^[0-9a-f]{64}$/.test(raw) ? raw : null;
}

/**
 * Discovery `navigator.credentials.get()` — used only when the browser has
 * no cached fingerprint. The challenge here is throwaway: we never send the
 * resulting assertion to the desktop, we only read `userHandle` to learn
 * the install fingerprint. The real auth assertion is run later inside
 * `respondToAuthChallenge` against a desktop-vended challenge.
 */
async function discoverFingerprint(): Promise<string | null> {
  const challenge = new Uint8Array(32);
  crypto.getRandomValues(challenge);
  const cred = (await navigator.credentials.get({
    publicKey: {
      challenge,
      // Empty allowCredentials lets the authenticator surface every
      // discoverable credential it has — the user picks the right one.
      allowCredentials: [],
      userVerification: 'preferred',
      timeout: 60_000,
    },
    mediation: 'optional' as CredentialMediationRequirement,
  })) as PublicKeyCredential | null;
  if (!cred) return null;
  const response = cred.response as AuthenticatorAssertionResponse;
  if (!response.userHandle) return null;
  return userHandleToFingerprint(response.userHandle);
}

async function respondToAuthChallenge(
  peer: Peer,
  challenge: PairAuthChallenge,
  els: UI
): Promise<void> {
  if (challenge.origin !== location.origin) {
    throw new Error(
      `Reconnect URL origin mismatch (page is ${location.origin}, desktop expected ${challenge.origin}).`
    );
  }
  setStatus(els.status, 'verifying', 'Waiting for biometric…');
  const cred = (await navigator.credentials.get({
    publicKey: {
      challenge: base64UrlToBytes(challenge.challenge),
      rpId: challenge.rpId,
      allowCredentials: [],
      userVerification: 'preferred',
      timeout: 60_000,
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error('Authenticator returned no credential.');
  await peer.sendEncryptedJson({
    type: 'pair:auth-response',
    authenticationResponse: assertionToJSON(cred),
  });
  setStatus(els.status, 'verifying', 'Verifying with desktop…');
}

// `bytesToBase64Url` is imported for use in the auth path's JSON shaping;
// re-export the registration helper inline so build noise stays low.
export { bytesToBase64Url };

void start();
